import ErrorPage from "@/components/pages/ErrorPage";
import { supabaseClient } from "@/lib/supabase";
import { useAuth } from "@/utils/auth";
import { z } from "@/utils/form";
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useMemo } from "react";
import Title from "../components/common/Title";
import LoginForm from "../features/auth/components/LoginForm";
import { PreviewPageHeader } from "@/features/previews/components/PreviewPageHeader";
import { PreviewHeaderProvider } from "@/features/previews/context/PreviewHeaderContext";
import { normalizePrintTokenFromSearch } from "@/features/previews/utils/normalizePrintTokenFromSearch";
import { cn } from "@/lib/utils";
import { useTheme } from "../hooks/useTheme";

/** JWT payloads use base64url; `atob` expects standard base64 and breaks the print token gate. */
function decodeJwtPayloadSegment(segment: string): Record<string, unknown> | null {
    try {
        const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
        const pad = (4 - (base64.length % 4)) % 4;
        const padded = base64 + "=".repeat(pad);
        const json = atob(padded);
        const data = JSON.parse(json) as Record<string, unknown>;
        return data && typeof data === "object" ? data : null;
    } catch {
        return null;
    }
}

/** Router search + window fallback (parent layout must see ?token= like the child loader). */
function readPrintTokenRaw(search: unknown): string | null {
    if (search && typeof search === "object" && "token" in search) {
        const t = (search as { token?: unknown }).token;
        if (typeof t === "string" && t.length > 0) return t;
    }
    if (typeof window !== "undefined") {
        const q = new URLSearchParams(window.location.search).get("token");
        if (q) return q;
    }
    return null;
}

function printTokenLooksValid(raw: string): boolean {
    const token = normalizePrintTokenFromSearch(raw);
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return false;
        const payloadSegment = parts[1];
        if (payloadSegment === undefined) return false;
        const payload = decodeJwtPayloadSegment(payloadSegment);
        if (!payload) return false;
        if (!payload.patientId || !payload.diaryId) return false;
        const exp = payload.exp;
        if (typeof exp === "number" && exp * 1000 < Date.now()) return false;
        return true;
    } catch {
        return false;
    }
}

function PreviewPage() {
    const auth = useAuth();
    const locationSearch = useRouterState({ select: (s) => s.location.search });
    const hasValidToken = useMemo(() => {
        const raw = readPrintTokenRaw(locationSearch);
        if (!raw) return false;
        return printTokenLooksValid(raw);
    }, [locationSearch]);

    const isConsentPreview = useRouterState({
        select: (state) => state.location.pathname.startsWith("/preview/consent"),
    });
    const { removeBackground } = useTheme();

    removeBackground();

    return (
        <>
            {/* Print styles to hide browser elements */}
            <style>
                {`
					@media print {
						@page {
							margin: 0;
							size: A4;
						}
						
						html,
						body,
						#app {
							background: rgb(224 242 255) !important;
							-webkit-print-color-adjust: exact;
							print-color-adjust: exact;
						}
						
						/* Hide browser print elements */
						body {
							-webkit-print-color-adjust: exact;
							print-color-adjust: exact;
						}
						
						/* Hide page numbers, URLs, headers, footers */
						@page :first {
							margin-top: 0;
						}
						
						@page :left {
							margin-left: 0;
						}
						
						@page :right {
							margin-right: 0;
						}

						/* 
							To simulate top padding on print when using a colored background (since @page margin won't work for full-bleed backgrounds), 
							add a transparent spacer at the top of your printable content using a print-only utility class.
							Example: <div className="hidden print:block h-[1in]" />
							No valid @page :top selector exists in CSS.
						*/
						
						/* Ensure no browser-added content */
						html {
							-webkit-print-color-adjust: exact;
							print-color-adjust: exact;
						}
					}
				`}
            </style>
            <PreviewHeaderProvider>
                <div
                    className={cn(
                        "flex flex-col w-full min-h-full",
                        isConsentPreview
                            ? "consent-preview-shell min-h-dvh print:min-h-0 bg-slate-100 dark:bg-slate-900 px-4 py-8 sm:px-6"
                            : "gap-y-8 print:gap-y-4 bg-sky-100 print:bg-sky-100 p-8 print:p-4 px-4"
                    )}
                >
                    {isConsentPreview ? null : <PreviewPageHeader />}
                    {auth.isAuthenticated || hasValidToken ? (
                        <Outlet />
                    ) : (
                        <>
                            <div id="print-login-required" data-reason="login-required" aria-hidden="true" />
                            <Title words="Print Page" description="To print the page, please first login." />
                            <LoginForm shouldNavigate={false} />
                        </>
                    )}
                    {isConsentPreview ? null : (
                        <footer className="print:hidden mt-auto pt-8 border-gray-200 dark:border-gray-800 border-t">
                            <div className="flex flex-col gap-2 text-gray-600 dark:text-gray-400 text-sm">
                                <div>For help and support please contact guy@patient-watch.com</div>
                                <div>© Patient Watch Ltd {dayjs().get("year")}</div>
                            </div>
                        </footer>
                    )}
                </div>
            </PreviewHeaderProvider>
        </>
    );
}

const previewSchema = z.object({
    accessToken: z.string().optional(),
    refreshToken: z.string().optional(),
    token: z.string().optional(),
});

export type PreviewSearchT = z.infer<typeof previewSchema>;

export const Route = createFileRoute("/preview")({
    component: PreviewPage,
    errorComponent: ({ error, reset }) => <ErrorPage title="Preview Error" error={error as Error} reset={reset} />,
    validateSearch: (search) => previewSchema.parse(search),
    loaderDeps: ({ search: { accessToken, refreshToken } }) => ({
        accessToken,
        refreshToken,
    }),
    loader: async ({ deps: { accessToken, refreshToken } }) => {
        // Only set session if both tokens are provided
        if (accessToken && refreshToken) {
            try {
                await supabaseClient.auth.setSession({
                    access_token: String(accessToken),
                    refresh_token: String(refreshToken),
                });
            } catch (error) {
                console.error("Failed to set auth session:", error);
                // Continue - don't throw the error as tokens are optional
            }
        }
    },
});
